import { UseFormReturn, useFieldArray } from "react-hook-form";
import { Film, Plus, Zap } from "lucide-react";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  RadioGroup,
  RadioCard,
  RadioCardItem,
  RadioCardContent,
  RadioCardDescription,
} from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
SwitchCard,
  SwitchCardSwitch,
  SwitchCardContent,
} from "@/components/ui/switch-card";
import { Button } from "@/components/ui/button";
import { useOrganizationSettingsStore } from "@/stores";
import tiktok from "@/assets/tiktok.svg";
import instagram from "@/assets/instagram.svg";
import youtube from "@/assets/youtube.svg";
import dub_logo_dark from "@/assets/dub_logo_dark.svg";
import dub_logo_light from "@/assets/dub_logo_light.svg";
import { CampaignFormData, Tier } from "./campaignFormTypes";
import TierCard from "./TierCard";

const PLATFORMS = [
  { id: "tiktok", label: "TikTok", icon: tiktok },
  { id: "instagram", label: "Instagram", icon: instagram },
  { id: "youtube", label: "Youtube", icon: youtube },
];

interface CampaignFormStep1Props {
  form: UseFormReturn<CampaignFormData>;
}

export default function CampaignFormStep1({ form }: CampaignFormStep1Props) {
  const dubUserId = useOrganizationSettingsStore(
    (state) => state.organizationInfo?.dub_user_id
  );
  const hasDubUser = !!dubUserId;

  const { fields: tierFields, append: appendTier, remove: removeTier } = useFieldArray({
    control: form.control,
    name: "tiers" as any,
  });

  const tiers = form.watch("tiers");

  // Show Dub toggle if any tier has affiliate-type components
  const hasAffiliateComponent = tiers?.some(
    (tier) => tier.tier_type === "affiliate" || tier.tier_type === "combined"
  );

  return (
    <>
      <FormField
        control={form.control}
        name="campaign_name"
        render={({ field }) => (
          <FormItem className="relative mb-6">
            <FormLabel>Campaign Name</FormLabel>
            <FormControl>
              <Input placeholder="A name to remember..." {...field} autoFocus />
            </FormControl>
            <FormDescription>Maximum 60 characters allowed.</FormDescription>
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="platform"
        render={({ field }) => (
          <FormItem className="mt-5">
            <FormLabel>Platform</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={(value) => {
                  field.onChange(value);
                  form.setValue("countries", []);
                  if (value === "youtube") {
                    form.setValue("youtube_content_type", "shorts");
                  } else {
                    form.setValue("youtube_content_type", undefined);
                  }
                }}
                defaultValue={field.value}
                className="grid grid-cols-1 lg:grid-cols-3 gap-2"
              >
                {PLATFORMS.map((platform) => (
                  <RadioCard key={platform.id} className="items-center gap-3">
                    <img
                      src={platform.icon || "/placeholder.svg"}
                      alt={platform.label.toLowerCase()}
                      className={`shrink-0 h-6 w-auto grayscale ${platform.id === "youtube" && "opacity-80 max-h-5"}`}
                    />
                    <Label htmlFor={platform.id} className="grow">
                      {platform.label}
                    </Label>
                    <RadioCardItem value={platform.id} id={platform.id} />
                  </RadioCard>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage className="text-xs" />
          </FormItem>
        )}
      />

      {form.watch("platform") === "youtube" && (
        <FormField
          control={form.control}
          name="youtube_content_type"
          render={({ field }) => (
            <FormItem className="mt-5">
              <FormLabel>Content Type</FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-2"
                >
                  <RadioCard className="items-center gap-3">
                    <Zap />
                    <RadioCardContent>
                      <Label htmlFor="yt_shorts">Shorts</Label>
                      <RadioCardDescription>
                        Short-form vertical videos
                      </RadioCardDescription>
                    </RadioCardContent>
                    <RadioCardItem value="shorts" id="yt_shorts" />
                  </RadioCard>
                  <RadioCard className="items-center gap-3">
                    <Film />
                    <RadioCardContent>
                      <Label htmlFor="yt_long_form">Long Form</Label>
                      <RadioCardDescription>Regular videos</RadioCardDescription>
                    </RadioCardContent>
                    <RadioCardItem value="long_form" id="yt_long_form" />
                  </RadioCard>
                </RadioGroup>
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
      )}

      {/* Partnership Tiers */}
      <div className="mt-5 space-y-3">
        <FormLabel>Partnership Tiers</FormLabel>
        <FormDescription>
          Define compensation offers. Amy will try each tier in order if a creator declines.
        </FormDescription>

        <div className="space-y-3">
          {tierFields.map((tierField, index) => (
            <TierCard
              key={tierField.id}
              form={form}
              tierIndex={index}
              isFirst={index === 0}
              onRemove={() => {
                removeTier(index);
                // Re-number remaining tiers
                const remaining = form.getValues("tiers");
                remaining.forEach((_: Tier, i: number) => {
                  form.setValue(`tiers.${i}.order` as any, i + 1);
                });
              }}
            />
          ))}
        </div>

        {tierFields.length < 4 && (
          <Button
            type="button"
            variant="outline"
            className="w-full border-dashed"
            onClick={() => {
              appendTier({
                order: tierFields.length + 1,
                tier_type: "paid",
                operator: null,
                components: [{ type: "max_budget" }],
              } as any);
            }}
          >
            <Plus className="size-4" /> Add Fallback Tier
          </Button>
        )}
      </div>

      {/* Dub Links Toggle — shown if any tier has affiliate components */}
      {hasAffiliateComponent && (
        <FormField
          control={form.control}
          name="use_dub"
          render={({ field }) => (
            <FormItem className="mt-5">
              <SwitchCard disabled={!hasDubUser}>
                <FormControl>
                  <SwitchCardSwitch
                    id="use-dub"
                    checked={field.value ?? false}
                    onCheckedChange={field.onChange}
                    disabled={!hasDubUser}
                    aria-describedby="use-dub-description"
                  />
                </FormControl>
                <SwitchCardContent className="flex flex-row items-center gap-2">
                  <img src={dub_logo_light} alt="Dub Icon" className="size-9 bg-sidebar border p-1 rounded-md dark:hidden" />
                  <img src={dub_logo_dark} alt="Dub Icon" className="size-9 bg-sidebar border p-1 rounded-md hidden dark:block" />
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="use-dub">
                      Enable Dub Links
                      <span className="text-muted-foreground text-xs"> (Optional)</span>
                    </Label>
                    <p
                      id="use-dub-description"
                      className="text-muted-foreground text-xs text-start"
                    >
                      {hasDubUser
                        ? "Generate tracked affiliate links using Dub for this campaign."
                        : "No Dub account connected. Configure in Settings \u2192 Organization."}
                    </p>
                  </div>
                </SwitchCardContent>
              </SwitchCard>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
}
